import { trackOpenAIContact } from "./openaiAttribution";
import { GA_MEASUREMENT_ID, canTrack, initializeGoogleTracking } from "./googleTracking";

/**
 * Centralized conversion tracking for alphaappraisals.ca.
 * Mirrors the Barberhood lib/trackBooking.js pattern.
 *
 * Fires GA4 events + Google Ads conversions for:
 *   - Appraisal order-form submissions (per form type, with an assigned lead value)
 *   - Homepage "Request Quote" submissions
 *   - Phone-call link clicks (tel:)
 *
 * Google Ads accounts (configured in lib/googleTracking.js):
 *   AW-10995985296  TORONTO
 *   AW-11144453197  PEEL
 *
 * GA4 and Google Ads events only fire on public production pages, so local,
 * preview, admin, and invoice activity does not pollute reporting.
 */

// ---- Google Ads conversion labels -------------------------------------------
// Form submission. These are the existing LIVE labels (currently fired on every
// order form). They stay shared for now; per-form-type conversion actions are a
// TODO in the account (create broker / probate / divorce / etc. actions, then
// swap the shared label below for FORM_CONFIG[type].awLabel*).
const AW_FORM_TORONTO = "AW-10995985296/xyKJCKLEs6YaEJDXpfso";
const AW_FORM_PEEL = "AW-11144453197/E7G6CM62oaYaEM24i8Ip";

// Phone-call lead (website tel: click). "Click to call" conversion actions
// created 2026-05-23 in both accounts (category Phone call lead, source
// "calls from website visits" -> "clicking a number on my website", so no
// Google forwarding number is used and Alpha's real number stays on the site).
const AW_PHONE_TORONTO = "AW-10995985296/A0qOCKXZ4LEcEJDXpfso";
const AW_PHONE_PEEL = "AW-11144453197/B4EeCOvm4LEcEM24i8Ip";

// ---- Per-form-type value (CAD) ----------------------------------------------
// value = an assigned lead value, not earned revenue, in CAD. Tiered to Alpha's
// real pricing (confirmed by Sehroz 2026-05-23): standard appraisals ~$450,
// higher-fee specialty work ~$600, top-of-funnel leads (quote/phone) ~$400.
// gaType = the GA4 form_type label for segmentation.
const FORM_CONFIG = {
  "broker-lender": { value: 450, gaType: "broker_lender" },
  buyers: { value: 450, gaType: "buyers" },
  "property-owner": { value: 450, gaType: "property_owner" },
  "lawyer-accountant-other": { value: 600, gaType: "lawyer_accountant_other" },
  "probate-estate": { value: 600, gaType: "probate_estate" },
  "power-of-sale": { value: 600, gaType: "power_of_sale" },
  "proposal-insolvency": { value: 600, gaType: "proposal_insolvency" },
  "request-quote": { value: 400, gaType: "request_quote" },
};
const DEFAULT_FORM = { value: 450, gaType: "appraisal_request" };

// Value attributed to a phone-call lead (top-of-funnel, lower commitment).
const PHONE_LEAD_VALUE = 400;
const CURRENCY = "CAD";

const recentEvents = new Map();
const DEDUP_WINDOW_MS = 10000;

function isRealLabel(sendTo) {
  return Boolean(sendTo) && !sendTo.includes("TODO");
}

// Enhanced Conversions: pass normalized contact data so Google can match the
// conversion to a signed-in user when Enhanced Conversions is enabled.
// Scope these identifiers to the Ads event; never store them globally.
function enhancedConversionData({ email, phone } = {}) {
  const userData = {};
  if (email) userData.email = String(email).trim().toLowerCase();
  if (phone) {
    const digits = String(phone).replace(/[^0-9]/g, "");
    if (digits) {
      userData.phone_number = digits.length === 10 ? `+1${digits}` : `+${digits}`;
    }
  }
  return userData;
}

function fireConversion({ eventName, dedupKey, transactionId, sendToList, value, payload, userData }) {
  if (typeof window === "undefined") return;

  // Keep development, preview, admin, and invoice activity out of GA4 and Ads.
  if (!canTrack()) return;

  // Dedup: skip if the same event+key fired within ten seconds (e.g. a
  // component handler and the global listener both reacting to one click).
  const now = Date.now();
  const key = `${eventName}:${dedupKey}`;
  for (const [eventKey, time] of recentEvents) {
    if (now - time >= DEDUP_WINDOW_MS) recentEvents.delete(eventKey);
  }
  if (recentEvents.has(key)) return;
  recentEvents.set(key, now);

  initializeGoogleTracking();

  // GA4 event.
  window.gtag("event", eventName, { ...payload, send_to: GA_MEASUREMENT_ID });
  const conversionData = enhancedConversionData(userData);

  for (const sendTo of sendToList) {
    if (!isRealLabel(sendTo)) continue;
    window.gtag("event", "conversion", {
      send_to: sendTo,
      value,
      currency: CURRENCY,
      transaction_id: transactionId,
      ...(Object.keys(conversionData).length ? { user_data: conversionData } : {}),
    });
  }
}

/**
 * Fire on a successful appraisal order-form / quote submission.
 * @param {string} formType  a FORM_CONFIG key, e.g. "probate-estate"
 * @param {object} data      form values (used for Enhanced Conversions)
 */
export function trackFormSubmit(formType, data = {}) {
  const cfg = FORM_CONFIG[formType] || DEFAULT_FORM;
  const email =
    data.Email || data.emailA || data["Email Address"] || data["Requesters Email"];
  const phone =
    data.Phone || data.phoneNumber || data["Phone Number"] || data["Requesters Number"];
  const dedupKey = [formType, email, phone]
    .map((part) => String(part || "").trim().toLowerCase())
    .join(":");
  const transactionId = `${formType || "form"}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

  fireConversion({
    eventName: "form_submission",
    dedupKey,
    transactionId,
    sendToList: [AW_FORM_TORONTO, AW_FORM_PEEL],
    value: cfg.value,
    payload: {
      form_type: cfg.gaType,
      form_variant: formType || "unknown",
      value: cfg.value,
      currency: CURRENCY,
      transaction_id: transactionId,
    },
    userData: { email, phone },
  });
}

/**
 * Fire on a tel: link click anywhere on the site.
 * @param {string} phone   the raw phone string from the href
 * @param {string} source  caller tag for GA4 / debugging
 */
export function trackPhoneClick(phone, source = "tel_link") {
  trackOpenAIContact("phone_click");
  const digits = String(phone || "").replace(/[^0-9]/g, "");
  const transactionId = `phone-${digits || "na"}-${Date.now()}`;

  fireConversion({
    eventName: "phone_call",
    dedupKey: digits || source,
    transactionId,
    sendToList: [AW_PHONE_TORONTO, AW_PHONE_PEEL],
    value: PHONE_LEAD_VALUE,
    payload: {
      event_category: "engagement",
      call_source: source,
      contact_method: "phone",
    },
  });
}

/**
 * Fire on a mailto: link click anywhere on the site. GA4 event only for now
 * (no Google Ads conversion action exists for email yet; it can be promoted to
 * an Ads conversion later the same way "Click to call" was).
 */
export function trackEmailClick(email, source = "mailto_link") {
  trackOpenAIContact("email_click");
  fireConversion({
    eventName: "email_click",
    dedupKey: email || source,
    transactionId: `email-${Date.now()}`,
    sendToList: [],
    value: 0,
    payload: {
      event_category: "engagement",
      call_source: source,
      contact_method: "email",
    },
  });
}

/**
 * Fire when a user clicks through to the Google Maps / GBP listing ("find us",
 * directions, or a review card). GA4 engagement event only.
 */
export function trackMapsClick(url, source = "maps_link") {
  fireConversion({
    eventName: "maps_click",
    dedupKey: url || source,
    transactionId: `maps-${Date.now()}`,
    sendToList: [],
    value: 0,
    payload: {
      event_category: "engagement",
      call_source: source,
      contact_method: "maps",
    },
  });
}

/** Credential verification is engagement, not a lead or Ads conversion. */
export function trackProfileClick() {
  fireConversion({
    eventName: "aic_profile_click", dedupKey: "mushtaq_aic_profile",
    transactionId: `profile-${Date.now()}`, sendToList: [], value: 0,
    payload: { event_category: "engagement", profile_provider: "aic" },
  });
}

/** Copying an address is distinct from opening an email application. */
export function trackEmailCopy() {
  fireConversion({
    eventName: "email_copy", dedupKey: "business_email",
    transactionId: `email-copy-${Date.now()}`, sendToList: [], value: 0,
    payload: { event_category: "engagement", contact_method: "email_copy" },
  });
}
